import { useParams } from 'react-router-dom';
import { Query } from '../../../components/Query';
import { api } from '../../../lib/api';
import { styled, Typography } from '@mui/material';
import { Section } from '../../../components/Section';
import { Info } from './components/Info';
import { paymentModeType } from '../types';
import { computePrices } from '../lib/computePrices';
import { TransferDetails } from '../components/TransferDetails';
import { PaymentModeLabel } from '../components/PaymentModeLabel';
import { computeReadableDate } from '../../../lib/date';
import { EsajSignature } from '../components/EsajSignature';
import { SignatureImage } from '../../../components/SignatureImage';
import { Button } from '../../../components/Button';
import { DisclaimerModal } from './components/DisclaimerModal';
import { useState } from 'react';
import {
    CURRENT_INSCRIPTION_YEAR,
    firstInscriptionYearType,
    yearMapping,
    yearType,
} from '../constants';
import { useQueryParams } from '../../../lib/useQueryParams';

function FicheInscriptionPreview() {
    const params = useParams();
    const queryParams = useQueryParams(['role']);
    const isRoleAdmin = queryParams.role === 'admin';
    const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(isRoleAdmin === false);
    if (!params.ficheInscriptionId) {
        return <div>Missing parameters in URL</div>;
    }

    const ficheInscriptionId = params.ficheInscriptionId;

    return (
        <Query
            queryKey={['formulaire-inscription-entries', ficheInscriptionId]}
            apiCall={() => api.getFicheInscriptionEntry(ficheInscriptionId)}
        >
            {(data) => {
                const readableDate = computeReadableDate(new Date(data.created_at));
                return (
                    <ContentContainer className="no-padding-print">
                        <Row>
                            <LogoContainer>
                                <Logo src="https://www.esaj.asso.fr/assets/images/logo-sos.svg" />
                            </LogoContainer>
                            <Column>
                                <Title variant="h2">BACHELOR / MASTER</Title>
                                <Subtitle variant="h2">
                                    Inscription - 
                                    {`${CURRENT_INSCRIPTION_YEAR}/${Number(CURRENT_INSCRIPTION_YEAR) + 1}`}
                                </Subtitle>
                            </Column>
                        </Row>
                        <BoldText variant="h3">
                            Inscription en {yearMapping[data.selectedPromo].label}
                        </BoldText>
                        {data.firstInscriptionYear === CURRENT_INSCRIPTION_YEAR && (
                            <BoldText variant="h3">
                                Il s'agit d'une première inscription à l'ésaj.
                            </BoldText>
                        )}
                        {data.firstInscriptionYear !== CURRENT_INSCRIPTION_YEAR && (
                            <BoldText variant="h3">
                                Année de première inscription :{' '}
                                {data.firstInscriptionYear === 'before2023'
                                    ? 'avant 2023'
                                    : data.firstInscriptionYear}
                            </BoldText>
                        )}
                        {data.firstInscriptionYear === null && (
                            <Typography variant="h6">
                                L'année de première inscription n'est pas renseignée.
                            </Typography>
                        )}
                        <BoldText variant="h3">
                            {data.hasAlreadyAnEsajMaster
                                ? "Le ou la candidat·e possède (✅) un Master à l'ésaj."
                                : "Le ou la candidat·e ne possède pas (❌) de Master à l'ésaj."}
                        </BoldText>
                        <Section variant="h2" title="Informations personnelles">
                            <Info label="Prénom" value={data.firstName.toUpperCase()} />
                            <Info label="Nom de famille" value={data.lastName.toUpperCase()} />
                            <Info label="Numéro INE" value={data.ineNumber} />
                            <Info label="Adresse e-mail" value={data.email} />
                        </Section>
                        <RegularRow>
                            <Section
                                variant="h2"
                                title="Informations du/de la responsable financier(e)"
                            >
                                <Info label="Prénom" value={data.rfFirstName.toUpperCase()} />
                                <Info
                                    label="Nom de famille"
                                    value={data.rfLastName.toUpperCase()}
                                />
                                <Info label="Adresse e-mail" value={data.rfEmail} />
                                <Info label="Adresse postale" value={data.rfAddress} />
                                <Info
                                    label="Téléphone portable personnel"
                                    value={data.rfMobilePhoneNumber}
                                />
                                <Info
                                    label="Téléphone professionnel"
                                    value={data.rfJobPhoneNumber}
                                />
                                <Info label="Profession" value={data.rfJob} />
                            </Section>
                            {!!data.rf2FirstName && (
                                <Section
                                    variant="h2"
                                    title="Informations du/de la deuxième responsable financier(e)"
                                >
                                    <Info
                                        label="Prénom"
                                        value={(data.rf2FirstName || '').toUpperCase()}
                                    />
                                    <Info
                                        label="Nom de famille"
                                        value={(data.rf2LastName || '').toUpperCase()}
                                    />
                                    <Info label="Adresse e-mail" value={data.rf2Email || ''} />
                                    <Info label="Adresse postale" value={data.rf2Address || ''} />
                                    <Info
                                        label="Téléphone portable personnel"
                                        value={data.rf2MobilePhoneNumber || ''}
                                    />
                                    <Info
                                        label="Téléphone professionnel"
                                        value={data.rf2JobPhoneNumber || ''}
                                    />
                                    <Info label="Profession" value={data.rf2Job || ''} />
                                </Section>
                            )}
                        </RegularRow>
                        <Section
                            variant="h2"
                            title="Modalités de paiement"
                            description="Le montant inclut les frais de scolarité et les frais de dossier (150 € non remboursables en cas de désistement avant le début de l'année académique)."
                        >
                            {renderPaymentModality(
                                data.selectedPaymentMode,
                                data.selectedPromo,
                                data.firstInscriptionYear,
                                data.hasAlreadyAnEsajMaster,
                            )}
                        </Section>
                        <Section variant="h2" title="Attestation">
                            <BoldText>
                                Je soussigné(e), {data.firstName.toUpperCase()}{' '}
                                {data.lastName.toUpperCase()}, confirme avoir pris connaissance des
                                tarifs et des modalités de paiement, et effectue par la présente mon
                                inscription à l’ésaj pour l’année scolaire{' '}
                                {CURRENT_INSCRIPTION_YEAR}/{Number(CURRENT_INSCRIPTION_YEAR) + 1}.
                            </BoldText>
                            <BoldText>
                                Je reconnais que l’inscription est annuelle et m’engage au règlement
                                de l’intégralité des frais de scolarité pour l’année 2026/2027, y
                                compris en cas d’abandon ou de désistement en cours d’année. Quel
                                que soit le mode de règlement choisi (comptant ou échelonné),
                                l’intégralité des frais de l’année demeure exigible dès lors que
                                l’inscription est effective.
                            </BoldText>
                            <BoldText>
                                Je reconnais avoir été informé(e) que, à défaut de règlement
                                intégral avant les échéances fixées, l’ésaj se réserve le droit de
                                suspendre ou de refuser mon accès aux enseignements, et ce jusqu’à
                                complète régularisation de ma situation financière.
                            </BoldText>

                            <Row>
                                <CenteredRow>
                                    <Column>
                                        <Typography>
                                            Fait à {data.place} le {readableDate}
                                        </Typography>
                                        <SignatureImage
                                            src={data.signatureUrl}
                                            alt="signature du candidat"
                                        />
                                    </Column>
                                </CenteredRow>
                                <EsajSignature />
                            </Row>
                        </Section>
                        <Row className="not-printable">
                            <Button fullWidth onClick={handlePrint} variant="contained">
                                Sauvegarder une copie du formulaire d'inscription
                            </Button>
                        </Row>
                        <DisclaimerModal
                            selectedPromo={data.selectedPromo}
                            isOpen={isDisclaimerModalOpen}
                            close={closeDisclaimerModal}
                        />
                    </ContentContainer>
                );
            }}
        </Query>
    );

    function closeDisclaimerModal() {
        setIsDisclaimerModalOpen(false);
    }

    function renderPaymentModality(
        paymentMode: paymentModeType,
        selectedPromo: yearType,
        firstInscriptionYear: firstInscriptionYearType | null,
        hasAlreadyAnEsajMaster: boolean,
    ) {
        const prices = computePrices({
            selectedPromo,
            firstInscriptionYear: firstInscriptionYear || CURRENT_INSCRIPTION_YEAR,
            hasAlreadyAnEsajMaster,
        });
        switch (paymentMode) {
            case 'comptant-cheque':
                return (
                    <>
                        <Info label="Répartition du paiement" value="Comptant" />
                        {prices.comptant.fee > 0 && (
                            <Info
                                label="Montant au moment de l'inscription"
                                value={`${prices.comptant.fee} €`}
                            />
                        )}
                        <Info
                            label={`Montant au ${prices.comptant.date}`}
                            value={`${prices.comptant.price} €`}
                        />
                        <Row>
                            <Typography>
                                Total annuel (frais de dossier + frais de scolarité) : 
                            </Typography>
                            <BoldText>{prices.annualPrice} €</BoldText>
                        </Row>
                        <Row>
                            <Typography>Mode de paiement :</Typography>
                            <BoldText>
                                <PaymentModeLabel paymentMode={paymentMode} />
                            </BoldText>
                        </Row>
                    </>
                );
            case 'comptant-virement':
                return (
                    <>
                        <Info label="Répartition du paiement" value="Comptant" />
                        {prices.comptant.fee > 0 && (
                            <Info
                                label="Montant au moment de l'inscription"
                                value={`${prices.comptant.fee} €`}
                            />
                        )}
                        <Info
                            label={`Montant au ${prices.comptant.date}`}
                            value={`${prices.comptant.price} €`}
                        />
                        <Row>
                            <Typography>
                                Total annuel (frais de dossier + frais de scolarité) : 
                            </Typography>
                            <BoldText>{prices.annualPrice} €</BoldText>
                        </Row>
                        <Row>
                            <Typography>Mode de paiement :</Typography>
                            <BoldText>
                                <PaymentModeLabel paymentMode={paymentMode} />
                            </BoldText>
                        </Row>
                        <Row>
                            <TransferDetails />
                        </Row>
                    </>
                );
            case 'echelonne-cheque':
                return (
                    <>
                        <Info label="Répartition du paiement" value="Échelonné" />

                        <Typography>Montants :</Typography>
                        <ul>
                            {prices.echelonne.fee > 0 && (
                                <li>
                                    <ListItemInfo>
                                        {prices.echelonne.fee} € au moment de l'inscription
                                    </ListItemInfo>
                                </li>
                            )}
                            {prices.echelonne.prices.map((payment) => (
                                <li key={payment.date}>
                                    <ListItemInfo>
                                        {payment.value.toFixed(0)} € au {payment.date}
                                    </ListItemInfo>
                                </li>
                            ))}
                        </ul>
                        <Row>
                            <Typography>
                                Total annuel (frais de dossier + frais de scolarité) : 
                            </Typography>
                            <BoldText>{prices.annualPrice} €</BoldText>
                        </Row>
                        <Row>
                            <Typography>Mode de paiement :</Typography>
                            <BoldText>
                                <PaymentModeLabel paymentMode={paymentMode} />
                            </BoldText>
                        </Row>
                    </>
                );
            case 'echelonne-virement':
                return (
                    <>
                        <Info label="Répartition du paiement" value="Échelonné" />
                        <Typography>Montants :</Typography>
                        <ul>
                            {prices.echelonne.fee > 0 && (
                                <li>
                                    <Typography>
                                        {prices.echelonne.fee} € au moment de l'inscription
                                    </Typography>
                                </li>
                            )}
                            {prices.echelonne.prices.map((payment) => (
                                <li key={payment.date}>
                                    <ListItemInfo>
                                        {payment.value.toFixed(0)} € au {payment.date}
                                    </ListItemInfo>
                                </li>
                            ))}
                        </ul>
                        <Row>
                            <Typography>
                                Total annuel (frais de dossier + frais de scolarité) : 
                            </Typography>
                            <BoldText>{prices.annualPrice} €</BoldText>
                        </Row>
                        <Row>
                            <Typography>Mode de paiement : </Typography>
                            <BoldText>
                                <PaymentModeLabel paymentMode={paymentMode} />
                            </BoldText>
                        </Row>

                        <Row>
                            <TransferDetails />
                        </Row>
                    </>
                );
        }
    }

    function handlePrint() {
        window.print();
    }
}

const BoldText = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}));

const Logo = styled('img')(({ theme }) => ({
    width: '100px',
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
}));
const Row = styled('div')(({ theme }) => ({
    display: 'flex',
    flex: 1,
    alignItems: 'center',
}));

const CenteredRow = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
}));

const ListItemInfo = styled(Typography)(({ theme }) => ({ fontWeight: 'bold' }));

const LogoContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(4),
}));

const ContentContainer = styled('div')(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(4),
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(8),
}));

const Title = styled(Typography)(({ theme }) => ({
    textAlign: 'center',
    paddingBottom: theme.spacing(1),
}));

const Subtitle = styled(Typography)(({ theme }) => ({
    textAlign: 'center',
    paddingBottom: theme.spacing(2),
}));

const RegularRow = styled('div')(({ theme }) => ({
    display: 'flex',
}));

const Column = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
}));

export { FicheInscriptionPreview };
